import React, { useState } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Alert, Pressable, Text, View } from "react-native";
import { Copy, Check, ChevronDown, ChevronUp, LogIn, Shield, ShieldCheck, User, ArrowLeft } from "lucide-react-native";
import { api } from "../api/client";
import { Button, Card, Field, PasswordInput, YNSLogo } from "../components/UI";
import { colors, styles } from "../styles";

export function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // MFA Flow State: "credentials" | "mfa_setup" | "mfa_verify"
  const [step, setStep] = useState("credentials");
  const [mfaData, setMfaData] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [showManualKey, setShowManualKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  const handleSuccessfulAuth = async (data) => {
    await AsyncStorage.setItem("auth_token", data.token);
    await AsyncStorage.setItem("auth_user", JSON.stringify(data.user));
    onLogin(data.user);
  };

  const submitCredentials = async () => {
    setErrorMsg("");
    try {
      setLoading(true);
      const data = await api.login(username, password);

      if (data.mfaSetupRequired) {
        setMfaData(data);
        setMfaCode("");
        setStep("mfa_setup");
      } else if (data.mfaRequired) {
        setMfaData(data);
        setMfaCode("");
        setStep("mfa_verify");
      } else if (data.token) {
        await handleSuccessfulAuth(data);
      }
    } catch (error) {
      setErrorMsg(error.message || "Invalid credentials provided.");
      Alert.alert("Login failed", error.message);
    } finally {
      setLoading(false);
    }
  };

  const submitMfaSetup = async () => {
    setErrorMsg("");
    if (!mfaCode || mfaCode.trim().length < 6) {
      setErrorMsg("Please enter the 6-digit code from your authenticator app.");
      return;
    }
    try {
      setLoading(true);
      const data = await api.mfaSetupVerify(mfaData.mfaToken, mfaCode.trim());
      await handleSuccessfulAuth(data);
    } catch (error) {
      setErrorMsg(error.message || "Verification failed. Please check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitMfaVerify = async () => {
    setErrorMsg("");
    if (!mfaCode || mfaCode.trim().length < 6) {
      setErrorMsg("Please enter the 6-digit code from your authenticator app.");
      return;
    }
    try {
      setLoading(true);
      const data = await api.mfaVerify(mfaData.mfaToken, mfaCode.trim());
      await handleSuccessfulAuth(data);
    } catch (error) {
      setErrorMsg(error.message || "Verification code is incorrect or expired.");
    } finally {
      setLoading(false);
    }
  };

  const copySecretKey = () => {
    if (mfaData?.secret && typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(mfaData.secret);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const backToLogin = () => {
    setStep("credentials");
    setMfaData(null);
    setMfaCode("");
    setErrorMsg("");
  };

  return (
    <View style={[styles.app, { justifyContent: "center", alignItems: "center", padding: 24, backgroundColor: colors.bg }]}>
      <Card style={{ width: "100%", maxWidth: 440, padding: 32, gap: 20 }}>
        {/* Brand Header */}
        <View style={{ alignItems: "center", gap: 8, paddingBottom: 4 }}>
          <YNSLogo width={220} height={50} showTagline={true} />
          <View style={{ alignItems: "center", marginTop: 4 }}>
            <Text style={[styles.h2, { textAlign: "center", fontSize: 22 }]}>Quotation Management System</Text>
            <Text style={[styles.small, { textAlign: "center", marginTop: 2, fontSize: 13 }]}>Enterprise Quotations & Invoicing</Text>
          </View>
        </View>

        {errorMsg ? (
          <Text style={[styles.message, styles.errorMessage]}>{errorMsg}</Text>
        ) : null}

        {/* STEP 1: USERNAME & PASSWORD */}
        {step === "credentials" && (
          <View style={{ gap: 16 }}>
            <Field
              label="Username"
              value={username}
              onChangeText={setUsername}
              placeholder="Enter username"
              icon={User}
            />
            <PasswordInput
              label="Password"
              value={password}
              onChangeText={setPassword}
              placeholder="Enter password"
            />

            <Button
              onPress={submitCredentials}
              icon={LogIn}
              disabled={loading || !username || !password}
              loading={loading}
              style={{ height: 48, marginTop: 4 }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </View>
        )}

        {/* STEP 2: MANDATORY MFA SETUP (ONBOARDING) */}
        {step === "mfa_setup" && (
          <View style={{ gap: 14 }}>
            <View style={{ alignItems: "center", gap: 6, paddingVertical: 4 }}>
              <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: "#E0F2FE", alignItems: "center", justifyContent: "center" }}>
                <ShieldCheck size={26} color={colors.primary} />
              </View>
              <Text style={[styles.h2, { fontSize: 18, textAlign: "center" }]}>Two-Factor Authentication Setup</Text>
              <Text style={[styles.small, { textAlign: "center", lineHeight: 18, color: colors.muted }]}>
                MFA is mandatory. Scan this QR code using Google Authenticator, Microsoft Authenticator, or Authy to configure your account.
              </Text>
            </View>

            {/* QR Code Container */}
            {mfaData?.qrCode ? (
              <View style={{ alignItems: "center", marginVertical: 6 }}>
                <div style={{
                  padding: 10,
                  backgroundColor: "#FFFFFF",
                  borderRadius: 12,
                  border: "1px solid " + colors.border,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  display: "inline-flex"
                }}>
                  <img
                    src={mfaData.qrCode}
                    alt="Authenticator QR Code"
                    style={{ width: 175, height: 175, display: "block" }}
                  />
                </div>
              </View>
            ) : null}

            {/* Manual Secret Key Accordion */}
            <View style={{ backgroundColor: colors.secondaryBg, borderRadius: 8, padding: 10, borderWidth: 1, borderColor: colors.border }}>
              <Pressable
                onPress={() => setShowManualKey(!showManualKey)}
                style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}
              >
                <Text style={[styles.small, { fontWeight: "600", color: colors.text }]}>
                  Can't scan the QR code? Use manual key
                </Text>
                {showManualKey ? <ChevronUp size={16} color={colors.muted} /> : <ChevronDown size={16} color={colors.muted} />}
              </Pressable>

              {showManualKey && mfaData?.secret ? (
                <View style={{ marginTop: 8, gap: 6 }}>
                  <Text style={[styles.small, { fontFamily: "monospace", fontSize: 12, backgroundColor: "#FFFFFF", padding: 8, borderRadius: 6, borderWidth: 1, borderColor: colors.border, textAlign: "center", userSelect: "all" }]}>
                    {mfaData.secret}
                  </Text>
                  <Pressable
                    onPress={copySecretKey}
                    style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 6, paddingVertical: 4 }}
                  >
                    {copiedKey ? <Check size={14} color="#16a34a" /> : <Copy size={14} color={colors.primary} />}
                    <Text style={[styles.small, { color: copiedKey ? "#16a34a" : colors.primary, fontWeight: "600" }]}>
                      {copiedKey ? "Copied to clipboard!" : "Copy Key"}
                    </Text>
                  </Pressable>
                </View>
              ) : null}
            </View>

            {/* Verification Code Input */}
            <Field
              label="Enter 6-Digit Authenticator Code *"
              value={mfaCode}
              onChangeText={setMfaCode}
              placeholder="e.g. 123456"
              maxLength={6}
              keyboardType="numeric"
              style={{ minWidth: "100%" }}
            />

            <Button
              onPress={submitMfaSetup}
              icon={ShieldCheck}
              disabled={loading || mfaCode.trim().length !== 6}
              loading={loading}
              style={{ height: 48, marginTop: 4 }}
            >
              {loading ? "Activating MFA..." : "Activate & Complete Sign In"}
            </Button>

            <Button variant="secondary" icon={ArrowLeft} onPress={backToLogin}>
              Back to Sign In
            </Button>
          </View>
        )}

        {/* STEP 3: MFA CHALLENGE (FOR CONFIGURED USERS) */}
        {step === "mfa_verify" && (
          <View style={{ gap: 16 }}>
            <View style={{ alignItems: "center", gap: 8, paddingVertical: 6 }}>
              <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: "#E0F2FE", alignItems: "center", justifyContent: "center" }}>
                <Shield size={28} color={colors.primary} />
              </View>
              <Text style={[styles.h2, { fontSize: 20, textAlign: "center" }]}>Two-Factor Authentication</Text>
              <Text style={[styles.small, { textAlign: "center", color: colors.muted, lineHeight: 18 }]}>
                Enter the 6-digit verification code from your authenticator app for{" "}
                <Text style={{ fontWeight: "700", color: colors.text }}>{mfaData?.name || mfaData?.username}</Text>.
              </Text>
            </View>

            <Field
              label="6-Digit Security Code *"
              value={mfaCode}
              onChangeText={setMfaCode}
              placeholder="123456"
              maxLength={6}
              keyboardType="numeric"
              style={{ minWidth: "100%" }}
            />

            <Button
              onPress={submitMfaVerify}
              icon={LogIn}
              disabled={loading || mfaCode.trim().length !== 6}
              loading={loading}
              style={{ height: 48, marginTop: 4 }}
            >
              {loading ? "Verifying..." : "Verify & Sign In"}
            </Button>

            <Button variant="secondary" icon={ArrowLeft} onPress={backToLogin}>
              Back to Sign In
            </Button>
          </View>
        )}

        {/* Footer */}
        <View style={{ borderTopWidth: 1, borderTopColor: colors.border, paddingTop: 16, marginTop: 4, alignItems: "center", gap: 4 }}>
          <Text style={[styles.small, { fontWeight: "600", color: colors.text }]}>Version 2.0 - Secured with TOTP MFA</Text>
          <Text style={styles.small}>© 2026 YNS Systems. All rights reserved.</Text>
          <View style={{ flexDirection: "row", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 4 }}>
            <Text style={[styles.small, { color: colors.muted }]}>
              Developed by <Text style={{ fontWeight: "700", color: colors.text }}>Zahid Munir</Text>
            </Text>
            <Text style={[styles.small, { color: colors.muted }]}>•</Text>
            <Text style={[styles.small, { color: colors.muted }]}>
              UI/UX Design by <Text style={{ fontWeight: "700", color: colors.text }}>Abdal Shah</Text>
            </Text>
          </View>
        </View>
      </Card>
    </View>
  );
}
